import {
  clientsMessageResponseSchema,
  clientsResponseSchema,
  type ClientFilter,
  type ClientsMessageRequest,
  type ClientsMessageResponse,
  type ClientsResponse,
} from "@teamna/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api, newIdempotencyKey } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * The Clients table (Pilates studio shape, C1), against `/v1/business/businesses/:id/clients`.
 * Owner and admin only: the API refuses staff.
 */

const base = (businessId: string) => `/business/businesses/${businessId}/clients`;

/** GET .../clients?filter=: the rows of one filter, and the count of every filter. */
export function useClients(businessId: string | null, filter: ClientFilter): UseQueryResult<ClientsResponse> {
  return useQuery({
    queryKey: queryKeys.setup.clients(businessId ?? "none", filter),
    queryFn: () => api.get(base(businessId ?? ""), clientsResponseSchema, { query: { filter } }),
    enabled: Boolean(businessId),
    // The chips keep their counts while the next filter loads.
    placeholderData: keepPreviousData,
  });
}

/** GET .../clients/export?filter=: the CSV file as text. */
export function useExportClients(businessId: string): UseMutationResult<string, Error, { filter: ClientFilter }> {
  return useMutation({
    mutationFn: ({ filter }) => api.getText(`${base(businessId)}/export`, { query: { filter } }),
  });
}

/** POST .../clients/message: one message into the thread of each chosen client. */
export function useMessageClients(businessId: string): UseMutationResult<ClientsMessageResponse, unknown, ClientsMessageRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post(`${base(businessId)}/message`, body, clientsMessageResponseSchema, { idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.conversations }),
  });
}
